import { useCallback } from "react"

import { useAppLocalization } from "../../modules/localization/components/AppLocalizationProvider"
import { useDateTime } from "../../modules/localization/useDateTime"

// All fields optional: the Postman examples show some meeting endpoints omit meetingTimeAsText,
// and the date times are only trusted after they parse.
type MeetingTimeDetails = Readonly<{
    startDateTime?: string
    endDateTime?: string
    meetingTimeAsText?: string
}>

/**
 * Localized meeting time label ("Today at 08:15 - 09:00") built from the meeting's start and end
 * date times. The API's meetingTimeAsText is English-only, so it is used only as the fallback
 * when the date times are missing or unparsable.
 */
export function useMeetingTimeLabel() {
    const dateTimeFactory = useDateTime()
    const { l10n } = useAppLocalization()

    return useCallback(
        (details: MeetingTimeDetails): string => {
            const start = dateTimeFactory.fromApiOrUndefined(details.startDateTime)
            const end = dateTimeFactory.fromApiOrUndefined(details.endDateTime)
            if (!start?.isValid || !end?.isValid) return details.meetingTimeAsText ?? ""

            const period = `${dateTimeFactory.formatTime(start)} - ${dateTimeFactory.formatTime(end)}`
            // Calendar dates compared as ISO strings, like meetingDayGroups does: the meeting is in
            // the API zone and now() in the browser zone, so a DateTime diff could come out fractional.
            const startIso = start.toISODate()
            const now = dateTimeFactory.now()
            if (startIso === now.toISODate()) return l10n.getString("meeting-time-label-today", { period })
            if (startIso === now.plus({ days: 1 }).toISODate())
                return l10n.getString("meeting-time-label-tomorrow", { period })
            if (startIso === now.minus({ days: 1 }).toISODate())
                return l10n.getString("meeting-time-label-yesterday", { period })
            return l10n.getString("meeting-time-label-date", { date: dateTimeFactory.formatLongDate(start), period })
        },
        [dateTimeFactory, l10n],
    )
}
